/**
 * CR-8716: Repeat Issue on the Create NCR form — a Yes/No the raiser sets.
 *
 * Pre-filled from the repeat check (same part number + fault group raised
 * before); the hint underneath says what the check found, so the raiser can
 * see where the suggestion came from and override it.
 */
import { Label } from '../../ui/label';
import type { RepeatInfo } from './ncrFormTypes';

interface RepeatIssueFieldProps {
  value: boolean;
  onChange: (value: boolean) => void;
  /** The latest repeat check; null until a part number and fault group are both filled in. */
  repeatInfo: RepeatInfo | null;
}

/**
 * The hint under the select.
 *
 * @param info - the latest repeat check, or null
 * @returns one line of help text
 */
function suggestion(info: RepeatInfo | null): string {
  if (!info) return 'Suggested automatically once a part number and fault group are entered.';
  if (!info.is_repeat) return 'Suggested: No — no previous claim for this part number + fault group.';
  const claims = `${info.previous_count} previous claim${info.previous_count === 1 ? '' : 's'}`;
  return `Suggested: Yes — ${claims} for this part number + fault group${info.last_ncr_number ? ` (last: ${info.last_ncr_number})` : ''}.`;
}

export default function RepeatIssueField({ value, onChange, repeatInfo }: RepeatIssueFieldProps) {
  return (
    <div className="max-w-xs">
      <Label htmlFor="is_repeat_issue">Repeat Issue</Label>
      <select
        id="is_repeat_issue"
        className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
        value={value ? 'yes' : 'no'}
        onChange={(e) => onChange(e.target.value === 'yes')}
        aria-describedby="is_repeat_issue-hint"
      >
        <option value="no">No</option>
        <option value="yes">Yes</option>
      </select>
      <p id="is_repeat_issue-hint" className="text-xs text-muted-foreground mt-1">{suggestion(repeatInfo)}</p>
    </div>
  );
}
